import { useEffect, useState } from 'react';
import { useVault } from '../session/useVault';
import { pendingText } from '../vault/writeQueue';
import { resolveId } from '../entity/catalog';
import type { CloudVault } from '../vault/cloudVault';
import type { KindVault } from '../vault/kinds';
import { findDeskContainer } from '../workspaces/loadWorkspaces';
import { DESK_ID, DESK_PATH, isDeskContainer } from './deskContainer';

export type DeskFile = { status: 'reading' } | { status: 'read'; raw: string | null };

/**
 * The container's file as it stands: what this tab still owes the vault, else the vault's copy at
 * [DESK_PATH], else where an older build renamed it: the file the catalog has its id at, or the one
 * the nooks are read from that is the container, for a catalog that does not know where it went.
 * Otherwise whatever [DESK_PATH] holds, null where nothing. A write to [DESK_PATH] follows it the
 * same ways. A vault whose catalog has no row for the container never had one, and its nooks are
 * not read for it: the app makes the container with a fixed time, so its row is in the shard
 * [resolveId] reads first.
 */
export async function readDeskRaw(vault: KindVault & Pick<CloudVault, 'readFiles'>): Promise<string | null> {
  const queued = pendingText(DESK_PATH);
  if (queued !== undefined) return queued;
  const raw = await vault.readFile(DESK_PATH);
  if (raw !== null && isDeskContainer(raw)) return raw;
  const entry = await resolveId(vault, DESK_ID);
  if (!entry) return raw;
  if (entry.path !== DESK_PATH) {
    const moved = pendingText(entry.path) ?? (await vault.readFile(entry.path));
    if (moved !== null && isDeskContainer(moved)) return moved;
  }
  return (await findDeskContainer(vault))?.text ?? raw;
}

/** Waits between tries after a read fails; the last one repeats. */
export const RETRY_MS = [1_000, 2_000, 5_000, 15_000, 60_000];

/**
 * The container's file as it stands ([readDeskRaw]); null only when the vault has no such file. Read
 * again when the vault state changes (a sync that brought in remote changes publishes a new one) and
 * when the write queue changes state. A read that fails keeps the last container read and is tried
 * again after a growing wait.
 */
export function useDeskContainer(): { file: DeskFile; show: (raw: string) => void } {
  const { state, queue } = useVault();
  const [file, setFile] = useState<DeskFile>({ status: 'reading' });

  useEffect(() => {
    if (state.status !== 'ready') return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const queued = pendingText(DESK_PATH);
    if (queued !== undefined) {
      setFile({ status: 'read', raw: queued });
      return;
    }
    const attempt = (failures: number) => {
      readDeskRaw(state.vault)
        .then((raw) => !cancelled && setFile({ status: 'read', raw }))
        .catch(() => {
          if (cancelled) return;
          timer = setTimeout(() => attempt(failures + 1), RETRY_MS[Math.min(failures, RETRY_MS.length - 1)]);
        });
    };
    attempt(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [state, queue.state]);

  return { file, show: (raw) => setFile({ status: 'read', raw }) };
}
